<template>
  <div class="relative w-full">
    <!-- Trigger/Field -->
    <div
        ref="trigger"
        class="min-h-12 w-full border rounded-lg bg-gray-800 shadow-sm cursor-pointer transition-all duration-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200"
        :class="disabled ? 'bg-gray-900 hover:border-gray-900' : 'hover:border-blue-900'"
        @click="openDialog"
        tabindex="0"
        role="button"
        :aria-haspopup="true"
        :aria-expanded="dialogOpen"
        :aria-controls="dialogOpen ? 'brand-modal' : null"
        dir="rtl"
    >
      <div class="flex flex-wrap gap-2 p-3">
        <div
            v-for="b in selectedBrands"
            :key="normalizeId(b.value)"
            class="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200"
            :class="disabled ? 'bg-gray-900 text-gray-100' : 'bg-blue-900 text-blue-100 hover:bg-blue-800'"
        >
          <span class="truncate max-w-[220px]">{{ b.name }}</span>
          <button
              v-if="!disabled"
              type="button"
              @click.stop="removeBrand(b.value)"
              class="transition-colors duration-200 text-lg leading-none"
              :class="disabled ? 'text-gray-100 hover:text-gray-800' : 'text-blue-200 hover:text-blue-200'"
              aria-label="حذف"
              title="حذف"
          >
            &times;
          </button>
        </div>
        <span v-if="selectedBrands.length === 0" class="text-gray-500 text-sm">
          {{ placeholder || 'برندها را انتخاب کنید' }}
        </span>
      </div>

      <!-- caret -->
      <div class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </div>
    </div>

    <transition name="fade-scale">
      <div
          ref="overlay"
          v-if="dialogOpen"
          id="brand-modal"
          class="modal-overlay"
          role="dialog"
          aria-modal="true"
          :aria-label="label || 'انتخاب برند'"
          @click="closeDialog"
      >
        <div
            ref="panel"
            class="dropdown-panel"
            @click.stop
            @keydown.esc.stop.prevent="closeDialog"
            @keydown.tab.prevent="handleTabTrap"
        >
          <div class="bg-white rounded-lg overflow-hidden shadow-xl w-full border border-gray-200" dir="rtl">
            <div class="bg-gray-900 px-4 pt-5 pb-4 sm:p-6 border-b border-gray-800 sticky top-0 z-10">

              <div class="flex items-start justify-between mb-4">
                <h3 class="text-lg leading-6 font-medium text-white text-center sm:text-right">
                  {{ createOpen ? 'ایجاد' : 'انتخاب' }}
                  برند
                </h3>

                <button
                    class="text-xs px-2 py-1 rounded-md bg-gradient-to-r from-green-600 to-cyan-600 text-white font-medium shadow hover:opacity-90 transition"
                    @click.stop="toggleCreateForm"
                >
                  {{ !createOpen ? 'ایجاد' : 'انصراف ' }}

                  برند
                  جدید

                </button>
              </div>

              <div class="mb-0" v-if="!createOpen">
                <input
                    type="text"
                    v-model="searchQuery"
                    placeholder="جستجو..."
                    class="text-right w-full rtl px-3 py-2 border bg-gray-700 border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-50"
                    dir="rtl"
                >
              </div>
            </div>

            <div
                v-if="createOpen"
                class="px-4 py-4 text-right bg-white"
                dir="rtl"
            >
              <div class="mb-4">
                <label class="block  text-xs font-medium text-gray-700 mb-1">نام برند *</label>
                <input
                    type="text"
                    v-model="createForm.name"
                    class="w-full px-3 py-2 border border-gray-300  bg-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    :class="{'border-red-400': createErrors.name}"
                    placeholder="نام برند را وارد کنید "
                >
                <p v-if="createErrors.name" class="text-xs text-red-600 mt-1">
                  {{ createErrors.name }}
                </p>
              </div>

              <div class="flex items-center justify-end gap-2">
                <button
                    class="px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                    @click="cancelCreateForm"
                    :disabled="createLoading"
                >
                  انصراف
                </button>

                <button
                    class="px-4 py-2 text-sm rounded-md font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow hover:opacity-90 flex items-center disabled:opacity-50"
                    @click="submitCreate"
                    :disabled="createLoading"
                >
                  <svg
                      v-if="createLoading"
                      class="animate-spin h-4 w-4 ml-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                  >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 100 16v4l3.5-3.5L12 20v4a8 8 0 01-8-8z"
                    ></path>
                  </svg>
                  <span>ثبت برند جدید</span>
                </button>
              </div>

              <p v-if="createErrors._error" class="text-xs text-red-600 mt-2 text-left ltr:text-left rtl:text-right">
                {{ createErrors._error }}
              </p>
            </div>
            <div class="tree-container overflow-y-auto px-2 sm:px-3 py-2" v-if="!createOpen">
              <div
                  v-for="brand in filteredBrands"
                  :key="normalizeId(brand.value)"
                  class="flex items-center gap-3 rounded-lg transition-colors duration-150 cursor-pointer border border-transparent hover:bg-gray-50 px-2 sm:px-3"
                  @click="toggleBrand(brand)"
              >
                <div
                    class="flex items-center justify-center border rounded-md transition-all duration-150 mr-1 ml-2"
                    style="width:30px; height:30px"
                    :class="isSelected(brand.value) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'"
                >
                  <svg v-if="isSelected(brand.value)" class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <span class="text-gray-800 text-sm font-medium text-right flex-1 truncate py-2">
                  {{ brand.name }}
                </span>
              </div>

              <div v-if="!loading && filteredBrands.length === 0" class="text-center py-6 text-gray-500 text-sm">
                برندی یافت نشد
              </div>
              <div v-if="loading" class="text-center py-5 text-gray-500 text-sm">در حال بارگذاری...</div>
            </div>

            <!-- Footer (sticky) -->
            <div
                v-if="!createOpen"
                class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 sticky bottom-0 z-10">
              <div class="mt-2 sm:mt-0 sm:mr-auto order-2 text-xs sm:text-sm text-gray-600">
                {{ tempSelectedIds.length }} برند انتخاب شده
              </div>
              <div class="flex items-left justify-end gap-3 order-1 w-full">
                <button
                    type="button"
                    class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition"
                    @click="closeDialog"
                >انصراف</button>
                <button
                    type="button"
                    class="px-4 py-2 border border-transparent rounded-md text-sm font-medium bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm hover:shadow transition"
                    @click="applySelection"
                >
                  <span class="text-gray-50">
                    اعمال انتخاب
                  </span>
                </button>

              </div>

            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'BrandSelect',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: [Array, Number, String, null], default: () => [] },
    label: { type: String, default: null },
    placeholder: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    presetItems: { type: Array, default: () => [] },
    scope: { type: String, default: 'local' }
  },
  data() {
    return {
      items: [],
      dialogOpen: false,
      searchQuery: '',
      tempSelectedIds: [],
      loading: false,
      _prevOverflow: '',
      _prevPaddingRight: '',
      _movedToBody: false,
      _prevBodyStyles: null,
      _lockY: 0,
      createOpen: false,
      createLoading: false,
      createErrors: {},
      createForm: {
        name: '',
      },

    };
  },
  computed: {
    selectedBrands() {
      const ids = this.currentIds();
      if (!ids.length) return [];
      const map = new Map(this.items.map(b => [this.normalizeId(b.value), b]));
      return ids.map(id => map.get(this.normalizeId(id))).filter(Boolean);
    },
    filteredBrands() {
      const q = (this.searchQuery || '').toLowerCase().trim();
      if (!q) return this.items;
      return this.items.filter(b => String(b.name || '').toLowerCase().includes(q));
    }
  },
  created() {
    if (this.presetItems.length) {
      this.items = this.presetItems.map(b => ({ name: b.name, value: b.id ?? b.value }));
    } else {
      this.getItems();
    }
  },
  mounted() {
    this.tempSelectedIds = this.currentIds();
  },
  beforeDestroy() {
    if (this.dialogOpen) this.unlockScroll();
  },
  methods: {
    toggleCreateForm() {
      // سوییچ بین حالت لیست و حالت ایجاد
      this.createOpen = !this.createOpen;

      // وقتی میریم روی ساخت، خطاهای قبلی رو پاک کن
      if (this.createOpen) {
        this.createErrors = {};
      }
    },

    cancelCreateForm() {
      // برگرد به حالت انتخاب لیست
      this.createOpen = false;
      this.createErrors = {};
      this.createForm = {
        name: '',
      };
    },

    submitCreate() {
      if (this.createLoading) return;
      this.createLoading = true;
      this.createErrors = {};

      const fd = new FormData();
      fd.append('name', this.createForm.name || '');

      this.request({
        url: this.endpoint('product/brand'),
        method: 'post',
        data: fd,
        headers: { 'Content-Type': 'multipart/form-data' },

        success: (data) => {

          this.items.push({
            name: data.name,
            value: data.id
          });

          // 2. همون لحظه انتخابش کن
          const newId = this.normalizeId(data.id);
          if (this.multiple) {
            if (!this.tempSelectedIds.some(x => this.eqId(x, newId))) {
              this.tempSelectedIds.push(newId);
            }
          } else {
            this.tempSelectedIds = [newId];
          }

          this.cancelCreateForm();

          // 4. اگه نوتیفیکیشن سیستمی داری
          if (typeof this.showNotificationMessage === 'function') {
            this.showNotificationMessage('برند با موفقیت ساخته شد');
          }

          this.createLoading = false;
        },

        error: (err) => {
          let payload = err && err.response && err.response.data ? err.response.data : err;
          if (!payload || typeof payload !== 'object') {
            payload = { _error: 'خطا در ثبت برند جدید' }
          }

          const out = {};
          for (const key in payload) {
            if (Array.isArray(payload[key])) {
              out[key] = payload[key].join(' ، ');
            } else if (typeof payload[key] === 'string') {
              out[key] = payload[key];
            } else {
              out[key] = 'نامعتبر';
            }
          }

          this.createErrors = out;
          this.createLoading = false;
        }
      });
    },
    normalizeId(id) { return id == null ? id : String(id); },
    eqId(a, b) { return this.normalizeId(a) === this.normalizeId(b); },
    currentIds() {
      if (this.multiple) return Array.isArray(this.value) ? this.value.map(this.normalizeId) : [];
      return this.value == null ? [] : [this.normalizeId(this.value)];
    },

    // API
    getItems() {
      this.loading = true;
      this.request({
        url: this.endpoint('product/brand'),
        method: 'get',
        success: (data) => {
          this.items = (data || []).map(brand => ({ name: brand.name, value: brand.id }));
          this.loading = false;
        },
        error: () => { this.loading = false; }
      });
    },

    openDialog() {
      if (this.disabled) return;
      // اگر انتخاب موقتی داری همین‌جا سینک کن
      // this.tempSelectedIds = this.currentIds?.() || [];
      this.dialogOpen = true;
      this.searchQuery = '';

      this.$nextTick(() => {
        // 1) انتقال نود مودال زیر body فقط یک‌بار
        const el = this.$refs.overlay;
        if (el && !this._movedToBody) {
          document.body.appendChild(el);
          this._movedToBody = true;
        }
        // 2) لاک اسکرول بدون پرش (position: fixed)
        this.lockScroll();

        // فوکوس روی سرچ (اگر ref داری)
        const input = this.$refs.searchInput;
        if (input && typeof input.focus === 'function') input.focus();
      });
    },

    closeDialog() {
      this.dialogOpen = false;
      this.searchQuery = '';
      // آزاد کردن اسکرول
      this.unlockScroll();

      // بازگردانی فوکوس به تریگر برای a11y
      this.$nextTick(() => {
        const trigger = this.$refs.trigger;
        if (trigger && typeof trigger.focus === 'function') trigger.focus();
      });
    },

    lockScroll() {
      const body = document.body;
      const docEl = document.documentElement;

      if (!this._prevBodyStyles) {
        this._prevBodyStyles = {
          position: body.style.position,
          top: body.style.top,
          left: body.style.left,
          right: body.style.right,
          width: body.style.width,
          paddingRight: body.style.paddingRight,
          overflow: body.style.overflow
        };
      }

      const hasScrollbar = window.innerWidth > docEl.clientWidth;
      const scrollbarWidth = hasScrollbar ? (window.innerWidth - docEl.clientWidth) : 0;

      this._lockY = window.scrollY || window.pageYOffset || 0;

      body.style.position = 'fixed';
      body.style.top = `-${this._lockY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (scrollbarWidth) body.style.paddingRight = `${scrollbarWidth}px`;
    },

    unlockScroll() {
      const body = document.body;
      const y = this._lockY || 0;

      if (this._prevBodyStyles) {
        body.style.position = this._prevBodyStyles.position || '';
        body.style.top = this._prevBodyStyles.top || '';
        body.style.left = this._prevBodyStyles.left || '';
        body.style.right = this._prevBodyStyles.right || '';
        body.style.width = this._prevBodyStyles.width || '';
        body.style.paddingRight = this._prevBodyStyles.paddingRight || '';
        body.style.overflow = this._prevBodyStyles.overflow || '';
      } else {
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.paddingRight = '';
        body.style.overflow = '';
      }

      window.scrollTo(0, y);
      this._prevBodyStyles = null;
      this._lockY = 0;
    },
    applySelection() {
      if (this.multiple) {
        const out = [...this.tempSelectedIds];
        this.$emit('input', out);
        this.$emit('change', out);
      } else {
        const val = this.tempSelectedIds[0] ?? null;
        this.$emit('input', val);
        this.$emit('change', val);
      }
      this.closeDialog();
    },
    toggleBrand(brand) {
      const id = this.normalizeId(brand.value);
      if (this.multiple) {
        const idx = this.tempSelectedIds.findIndex(x => this.eqId(x, id));
        if (idx > -1) this.tempSelectedIds.splice(idx, 1);
        else this.tempSelectedIds.push(id);
      } else {
        this.tempSelectedIds = [id];
      }
    },
    isSelected(brandValue) {
      const id = this.normalizeId(brandValue);
      return this.tempSelectedIds.some(x => this.eqId(x, id));
    },
    removeBrand(brandValue) {
      const id = this.normalizeId(brandValue);
      if (this.multiple) {
        const next = this.currentIds().filter(x => !this.eqId(x, id));
        this.$emit('input', next);
        this.$emit('change', next);
        this.tempSelectedIds = [...next];
      } else {
        this.$emit('input', null);
        this.$emit('change', null);
        this.tempSelectedIds = [];
      }
    },

    // Focus trap (basic)
    handleTabTrap(e) {
      const focusables = this.getFocusableElements(this.$refs.panel);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
      } else {
        // allow native tab
        e.preventDefault();
        const idx = focusables.indexOf(document.activeElement);
        const next = e.shiftKey ? (idx <= 0 ? last : focusables[idx - 1]) : (idx === focusables.length - 1 ? first : focusables[idx + 1]);
        next.focus();
      }
    },
    getFocusableElements(root) {
      if (!root) return [];
      const selectors = [
        'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
        'input:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])'
      ].join(',');
      return Array.from(root.querySelectorAll(selectors)).filter(el => el.offsetParent !== null);
    }
  },
  watch: {
    value() { this.tempSelectedIds = this.currentIds(); }
  }
};
</script>

<style scoped>
/* Animation */
.fade-scale-enter-active, .fade-scale-leave-active { transition: opacity 160ms ease, transform 160ms ease; }
.fade-scale-enter, .fade-scale-leave-to { opacity: 0; transform: scale(.98); }

/* Modal overlay & panel (exactly مثل نمونه دسته‌بندی) */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(17, 24, 39, 0.45);
  backdrop-filter: blur(2px);
}
.dropdown-panel {
  width: min(90vw, 520px);
  max-height: min(80dvh, 640px);
  box-shadow: 0 25px 50px -12px rgba(0,0,0,.25);
  border-radius: 12px;
  overflow: hidden;
  z-index: 9999;
  background: white;
}

.modal-overlay, .dropdown-panel {
  font-family: "iranyekan", "Vazirmatn", sans-serif !important;

}
.tree-container {
  max-height: calc(min(80dvh, 640px) - 200px);
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f3f4f6;
}
.tree-container::-webkit-scrollbar { width: 8px; }
.tree-container::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 4px; }
.tree-container::-webkit-scrollbar-thumb { background-color: #cbd5e0; border-radius: 4px; }
</style>
